/* ============================================================
   ZEPPELIN — Motion engine
   Moteur d'animation maison : révélations au scroll, découpe de
   texte (lignes / mots), progression liée au scroll, parallaxe,
   rails glissables, révélation d'image au curseur.
   Aucune dépendance externe. Un seul rAF, un seul listener scroll.
   ============================================================ */

(function () {
  'use strict';

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduce = reduceQuery.matches;
  var isCoarse = window.matchMedia('(hover: none)').matches;

  /* ── Boucle de rendu partagée ───────────────────────────── */

  var scrubbed = []; // éléments dont la progression suit le scroll
  var frame = null;

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(render);
  }

  function render() {
    frame = null;
    var vh = window.innerHeight;
    for (var i = 0; i < scrubbed.length; i++) scrubbed[i](vh);
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  /* ── 1. Découpe de texte (mots / lignes) ────────────────── */

  // Enveloppe chaque mot dans un span, en conservant les balises inline
  // de premier niveau (em, i, strong, br) présentes dans le titre.
  function wrapWords(node, out) {
    var kids = Array.prototype.slice.call(node.childNodes);
    kids.forEach(function (child) {
      if (child.nodeType === 3) {
        var parts = child.textContent.split(/(\s+)/);
        var frag = document.createDocumentFragment();
        parts.forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(' '));
            return;
          }
          var span = document.createElement('span');
          span.className = 'lf-w';
          span.textContent = part;
          out.push(span);
          frag.appendChild(span);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        wrapWords(child, out);
      }
    });
  }

  /* Un titre saisi dans l'editeur peut porter un lien, une emphase ou un
     gras. Le regroupement en lignes sort chaque mot de son parent pour le
     ranger dans sa ligne : le `<a>` etait vide puis jete avec le reste, le
     lien disparaissait du titre et `<em>` perdait son italique. On note
     donc, pour chaque mot, les balises en ligne qui l'entourent, et on les
     reconstruit dans la ligne. Les balises de bloc (p, div) restent
     aplaties : une ligne ne peut pas contenir de paragraphe. */
  var EN_LIGNE = /^(A|ABBR|B|CODE|DEL|EM|I|INS|MARK|Q|S|SMALL|SPAN|STRONG|SUB|SUP|TIME|U)$/;

  function ancetresEnLigne(mot, racine) {
    var chaine = [];
    for (var n = mot.parentNode; n && n !== racine; n = n.parentNode) {
      if (EN_LIGNE.test(n.tagName)) chaine.unshift(n);
    }
    return chaine;
  }

  /* Le decoupage se fait en trois temps, et l'ordre est tout.

     Chaque element demande d'ecrire (envelopper les mots), puis de lire
     (`offsetTop`, pour savoir ou tombent les lignes). Traiter les elements
     l'un apres l'autre enchaine ecriture, lecture, ecriture, lecture : la
     lecture qui suit une ecriture oblige le navigateur a recalculer toute
     la mise en page. Sur une page collection, vingt-quatre titres, donc
     vingt-quatre recalculs complets — Lighthouse mesurait 7,1 s de
     « Style & Layout » et signalait un reflow force a chaque rapport.

     On groupe donc : toutes les ecritures, puis toutes les lectures, puis
     toutes les ecritures. Un seul recalcul pour la page entiere. */

  function preparerMots(el) {
    if (!el.dataset.lfOriginal) el.dataset.lfOriginal = el.innerHTML;
    else el.innerHTML = el.dataset.lfOriginal;
    var mode = el.dataset.lfSplit || 'words';
    var words = [];
    wrapWords(el, words);
    return { el: el, mode: mode, words: words };
  }

  function finirMots(t) {
    t.words.forEach(function (w, i) { w.style.setProperty('--i', i); });
    t.el.style.setProperty('--n', t.words.length);
    t.el.classList.add('lf-split', 'lf-split--words');
  }

  function finirLignes(t) {
    var el = t.el;
    el.classList.add('lf-split', 'lf-split--lines');
    var lines = [];
    var currentTop = null;
    t.tops.forEach(function (top, k) {
      if (currentTop === null || Math.abs(top - currentTop) > 4) {
        currentTop = top;
        lines.push([]);
      }
      lines[lines.length - 1].push(k);
    });

    // A relever avant de deplacer le moindre mot.
    var chaines = t.words.map(function (w) { return ancetresEnLigne(w, el); });
    var liensVus = [];

    var frag = document.createDocumentFragment();
    lines.forEach(function (line, i) {
      var mask = document.createElement('span');
      mask.className = 'lf-line';
      var inner = document.createElement('span');
      inner.className = 'lf-line__i';
      inner.style.setProperty('--i', i);

      // Pile des balises ouvertes dans la ligne : deux mots voisins d'un
      // meme lien partagent la meme copie, l'espace entre eux compris.
      var pile = [];
      line.forEach(function (k, j) {
        var chaine = chaines[k];
        var commun = 0;
        while (commun < pile.length && commun < chaine.length && pile[commun].orig === chaine[commun]) commun++;
        pile.length = commun;
        if (j > 0) (commun ? pile[commun - 1].copie : inner).appendChild(document.createTextNode(' '));
        for (var c = commun; c < chaine.length; c++) {
          var copie = chaine[c].cloneNode(false);
          // Un lien coupe par un retour a la ligne devient deux liens :
          // la tabulation ne s'arrete que sur le premier.
          if (copie.tagName === 'A') {
            if (liensVus.indexOf(chaine[c]) !== -1) copie.setAttribute('tabindex', '-1');
            else liensVus.push(chaine[c]);
          }
          (c ? pile[c - 1].copie : inner).appendChild(copie);
          pile.push({ orig: chaine[c], copie: copie });
        }
        (pile.length ? pile[pile.length - 1].copie : inner).appendChild(t.words[k]);
      });
      mask.appendChild(inner);
      frag.appendChild(mask);
    });
    el.innerHTML = '';
    el.appendChild(frag);
    el.style.setProperty('--n', lines.length);
  }

  function splitTous(els) {
    // Les lignes se devinent a la position des mots : la taille doit etre
    // arretee avant la mesure.
    ajusterMegas();
    var travaux = [];
    els.forEach(function (el) { travaux.push(preparerMots(el)); });   // ecritures
    travaux.forEach(function (t) {                                    // lectures
      if (t.mode !== 'words') {
        t.tops = t.words.map(function (w) { return Math.round(w.offsetTop); });
      }
    });
    travaux.forEach(function (t) {                                    // ecritures
      if (t.mode === 'words') finirMots(t);
      else finirLignes(t);
    });
    // Les lignes existent : la vitrine peut etre mesuree.
    ajusterVitrines();
  }

  /* Le titre mega est regle en pourcentage, sans aucun rapport avec la
     longueur du texte : « La Fonderie 1890 » debordait de 24 % et le
     dernier mot tombait seul sur une deuxieme ligne. On mesure la chaine
     a une taille de reference, puis on plafonne la taille reelle pour
     qu'elle tienne sur une ligne. Un comptage de caracteres ne pouvait
     pas servir : entre « illillil » et « WWWWWWWW » la largeur par
     caractere va de 0,15 a 0,76 em dans cette fonte. */

  function megasTitres() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.lf-ov-frame--mega .lf-slide__heading')
    );
  }

  /* Le titre de la vitrine flottante est dimensionne par la largeur de
     l'ecran et par l'echelle reglee dans l'editeur, jamais par la longueur
     du texte. L'echelle de 155 % est calibree sur « Cuisson douce », qui
     tombe sur deux lignes ; l'allemand « Schongaren » est un mot insecable
     et debordait de 100 px de chaque cote. On mesure les lignes une fois
     decoupees et on plafonne la taille pour qu'elles tiennent. */

  function ajusterVitrines() {
    var els = Array.prototype.slice.call(document.querySelectorAll('.lf-float__title'));
    if (!els.length) return;

    // Ecritures : on repart de la taille reglee avant toute mesure.
    els.forEach(function (el) { el.style.removeProperty('--lf-float-fit'); });

    // Lectures. La boite du titre s'elargit a son contenu — elle mesurait
    // 1542 px pour un conteneur de 1440 — donc elle ne peut pas servir de
    // reference. La largeur utile est celle du parent, padding deduit.
    var mesures = els.map(function (el) {
      var parent = el.parentElement;
      if (!parent) return null;
      var cs = getComputedStyle(parent);
      var dispo = parent.clientWidth
                - (parseFloat(cs.paddingLeft) || 0)
                - (parseFloat(cs.paddingRight) || 0);
      var lignes = el.querySelectorAll('.lf-line__i');
      var large = 0;
      if (lignes.length) {
        Array.prototype.forEach.call(lignes, function (l) {
          // Une ligne sur deux est decalee de 8 % : ce retrait mange la place.
          var retrait = parseFloat(getComputedStyle(l.parentNode).paddingInlineStart) || 0;
          large = Math.max(large, l.scrollWidth + retrait);
        });
      } else {
        large = el.scrollWidth;
      }
      return { el: el, dispo: dispo, large: large };
    });

    // Ecritures.
    mesures.forEach(function (m) {
      if (!m || !m.dispo || !m.large || m.large <= m.dispo) return;
      /* Plancher : au-dela, mieux vaut laisser deborder que rendre le titre
         minuscule — c'est un titre d'affichage. */
      var ratio = Math.max(0.5, (m.dispo * 0.98) / m.large);
      m.el.style.setProperty('--lf-float-fit', ratio.toFixed(4));
    });
  }

  function ajusterMegas() {
    var els = megasTitres();
    if (!els.length) return;

    // Ecritures : on repart de la taille reglee avant toute mesure.
    els.forEach(function (el) { el.style.removeProperty('--lf-mega-fit'); });

    // En etroit, la coupe reste la bonne reponse : tenir sur une ligne
    // reduirait le titre a rien.
    if (window.innerWidth < 750) return;

    // Lectures.
    var mesures = els.map(function (el) {
      var cs = getComputedStyle(el);
      var taille = parseFloat(cs.fontSize);
      var dispo = el.clientWidth;
      var ref = 100;
      var sonde = document.createElement('span');
      sonde.style.cssText =
        'position:absolute;left:-9999px;top:0;white-space:pre;visibility:hidden;pointer-events:none';
      sonde.style.fontFamily = cs.fontFamily;
      sonde.style.fontWeight = cs.fontWeight;
      sonde.style.fontStyle = cs.fontStyle;
      sonde.style.fontStretch = cs.fontStretch;
      sonde.style.fontVariationSettings = cs.fontVariationSettings;
      sonde.style.textTransform = cs.textTransform;
      sonde.style.fontSize = ref + 'px';
      // L'interlettrage est en em : il doit suivre la taille de reference.
      var ls = parseFloat(cs.letterSpacing);
      sonde.style.letterSpacing = (taille ? (ls / taille) * ref : 0) + 'px';
      sonde.textContent = el.textContent.replace(/\s+/g, ' ').trim();
      document.body.appendChild(sonde);
      var large = sonde.getBoundingClientRect().width;
      sonde.parentNode.removeChild(sonde);
      return { el: el, dispo: dispo, parPx: large / ref, taille: taille };
    });

    // Ecritures.
    mesures.forEach(function (m) {
      if (!m.dispo || !m.parPx || !m.taille) return;
      var permise = (m.dispo * 0.995) / m.parPx;
      if (permise >= m.taille) return;
      // Plancher : au-dela, mieux vaut laisser couper que rendre illisible.
      var ratio = Math.max(0.55, permise / m.taille);
      m.el.style.setProperty('--lf-mega-fit', ratio.toFixed(4));
    });
  }

  function initMega() {
    if (!megasTitres().length) return;
    ajusterMegas();
    // La mesure depend de la fonte d'affichage : avant son chargement,
    // ce sont les metriques de secours qui sont lues.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { ajusterMegas(); ajusterVitrines(); });
    }
    var derniere = window.innerWidth;
    var minuteur;
    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth === derniere) return;
        derniere = window.innerWidth;
        clearTimeout(minuteur);
        minuteur = setTimeout(function () { ajusterMegas(); ajusterVitrines(); }, 180);
      },
      { passive: true }
    );
  }

  function initSplit() {
    var els = document.querySelectorAll('[data-lf-split]');
    if (!els.length) return;

    splitTous(Array.prototype.slice.call(els));

    // Les lignes dépendent de la largeur : on recalcule au redimensionnement.
    var lineEls = Array.prototype.filter.call(els, function (el) {
      return el.dataset.lfSplit === 'lines';
    });
    if (!lineEls.length) return;

    function recoupe() {
      splitTous(lineEls);
      lineEls.forEach(function (el) {
        /* Un recoupage n'est pas une entree en scene : c'est une correction
           de mise en page. Sans cette marque, detruire puis recreer les
           lignes relançait l'animation depuis l'invisible — et comme le
           recoupage attend la police, le titre disparaissait puis
           revenait tard. Lighthouse le mesurait comme le plus grand
           element peint, a 12,1 s. */
        el.classList.add('lf-split--pose');
      });
    }

    // Le découpage mesure la position de chaque mot pour deviner où tombent
    // les lignes. Tant que la police d'affichage n'est pas chargée, ce sont
    // les métriques de la police de secours qui sont mesurées : les lignes
    // tombent au mauvais endroit, et le bloc de titre n'a pas la bonne
    // hauteur. Au rafraîchissement suivant la police est en cache, elle est
    // là au moment de la mesure, et tout paraît correct — d'où un défaut
    // qui ne se voit qu'à la première visite. On recoupe donc dès que les
    // polices sont prêtes.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recoupe);
    }

    var lastWidth = window.innerWidth;
    var timer;
    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        clearTimeout(timer);
        timer = setTimeout(recoupe, 180);
      },
      { passive: true }
    );
  }

  /* ── 2. Révélations à l'entrée dans le viewport ─────────── */

  function initReveal() {
    var els = document.querySelectorAll('[data-lf-reveal]');
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('lf-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('lf-in');
          observer.unobserve(entry.target);
        });
      },
      /* La marge basse etait negative (-8 %) : la fenetre d'observation
         etait plus courte que l'ecran, et une section devait entrer
         franchement avant d'etre revelee. Le visiteur voyait donc du
         vide a l'endroit meme ou il regardait. Positive, la fenetre
         deborde sous l'ecran et la revelation est deja lancee quand la
         section arrive. */
      { threshold: 0.15, rootMargin: '0px 0px 10% 0px' }
    );

    els.forEach(function (el) {
      // indexation automatique des enfants pour les cascades
      var stagger = el.querySelectorAll('[data-lf-stagger]');
      stagger.forEach(function (child, i) {
        child.style.setProperty('--i', i);
      });
      observer.observe(el);
    });
  }

  /* ── 3. Progression liée au scroll (--p de 0 à 1) ───────── */

  function initScrub() {
    var els = document.querySelectorAll('[data-lf-scrub]');
    if (!els.length) return;

    if (reduce) {
      els.forEach(function (el) {
        el.style.setProperty('--p', 1);
      });
      return;
    }

    els.forEach(function (el) {
      var mode = el.dataset.lfScrub || '';

      // Mode « pin » : la section est plus haute que l'écran et reste figée
      // pendant qu'on la traverse. La progression court de 0 quand son haut
      // touche le haut de l'écran, à 1 quand son bas le rejoint.
      if (mode === 'pin') {
        var lastPin = -1;

        /* Une piste figee ne montre qu'une etape a la fois ; les autres sont
           decalees hors champ mais restent lisibles et atteignables au
           clavier. Un lien d'une etape eloignee recevait donc le focus sans
           jamais paraitre. On ramene le defilement la ou cette etape est au
           centre. Les etapes portent `data-lf-pin-step`, la scene figee
           `data-lf-pin-scene` ; hors disposition figee (etroit, animations
           reduites), la scene n'est plus collante et on ne touche a rien. */
        el.addEventListener('focusin', function (e) {
          var etape = e.target.closest('[data-lf-pin-step]');
          var scene = el.querySelector('[data-lf-pin-scene]');
          if (!etape || !scene || getComputedStyle(scene).position !== 'sticky') return;
          var etapes = el.querySelectorAll('[data-lf-pin-step]');
          var n = etapes.length;
          var i = Array.prototype.indexOf.call(etapes, etape);
          var rect = el.getBoundingClientRect();
          var travel = rect.height - window.innerHeight;
          if (n < 2 || i < 0 || travel <= 0) return;
          scene.scrollLeft = 0;
          window.scrollTo(0, window.scrollY + rect.top + travel * (i / (n - 1)));
        });

        scrubbed.push(function (vh) {
          var rect = el.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > vh) return;
          var travel = rect.height - vh;
          var p = travel <= 0 ? 0 : clamp01(-rect.top / travel);
          p = Math.round(p * 10000) / 10000;
          if (p === lastPin) return;
          lastPin = p;
          el.style.setProperty('--p', p);
        });
        return;
      }

      // Mode « exit » : pour un élément déjà en haut de page (le hero).
      // La progression suit sa sortie de l'écran, 0 tant qu'il est en place.
      if (mode === 'exit') {
        var lastExit = -1;
        scrubbed.push(function (vh) {
          var rect = el.getBoundingClientRect();
          if (rect.bottom < -vh || rect.top > vh) return;
          var p = rect.height <= 0 ? 0 : clamp01(-rect.top / rect.height);
          p = Math.round(p * 1000) / 1000;
          if (p === lastExit) return;
          lastExit = p;
          el.style.setProperty('--p', p);
        });
        return;
      }

      // Mode « start,end » (fractions de hauteur de viewport) : la progression
      // court du « haut de l'élément à start × vh » jusqu'au « bas de
      // l'élément à end × vh ». Par défaut 0.85 → 0.3.
      var cfg = mode.split(',');
      var startAt = parseFloat(cfg[0]);
      var endAt = parseFloat(cfg[1]);
      if (isNaN(startAt)) startAt = 0.85;
      if (isNaN(endAt)) endAt = 0.3;

      var last = -1;
      scrubbed.push(function (vh) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -vh || rect.top > vh * 2) return;
        var start = vh * startAt;
        var end = vh * endAt - rect.height;
        var span = start - end;
        var p = span <= 0 ? 1 : clamp01((start - rect.top) / span);
        p = Math.round(p * 1000) / 1000;
        if (p === last) return;
        last = p;
        el.style.setProperty('--p', p);
      });
    });
  }

  /* ── 4. Parallaxe verticale ─────────────────────────────── */

  function initParallax() {
    var els = document.querySelectorAll('[data-lf-parallax]');
    if (!els.length || reduce) return;

    els.forEach(function (el) {
      var speed = parseFloat(el.dataset.lfParallax);
      if (isNaN(speed)) speed = 0.15;
      var last = null;
      scrubbed.push(function (vh) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -vh * 0.5 || rect.top > vh * 1.5) return;
        var center = rect.top + rect.height / 2;
        var offset = Math.round((vh / 2 - center) * speed * 100) / 100;
        if (offset === last) return;
        last = offset;
        el.style.setProperty('--lf-py', offset + 'px');
      });
    });
  }

  /* ── 5. Liste index : image suivant le curseur ──────────── */

  function initIndexList() {
    document.querySelectorAll('[data-lf-index]').forEach(function (list) {
      var preview = list.querySelector('.lf-idx__preview');
      if (!preview) return;
      var rows = list.querySelectorAll('.lf-idx__row');
      var bounds = null;
      var targetX = 0;
      var targetY = 0;
      var x = 0;
      var y = 0;
      var running = false;
      var active = false;

      function loop() {
        x += (targetX - x) * 0.12;
        y += (targetY - y) * 0.12;
        preview.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
        if (active || Math.abs(targetX - x) > 0.5 || Math.abs(targetY - y) > 0.5) {
          requestAnimationFrame(loop);
        } else {
          running = false;
        }
      }

      function start() {
        if (running) return;
        running = true;
        requestAnimationFrame(loop);
      }

      if (!isCoarse && !reduce) {
        list.addEventListener('pointerenter', function (e) {
          bounds = list.getBoundingClientRect();
          // On cale l'aperçu sur le curseur dès l'entrée : sans cela il
          // resterait au coin supérieur gauche jusqu'au premier déplacement.
          targetX = e.clientX - bounds.left;
          targetY = e.clientY - bounds.top;
          x = targetX;
          y = targetY;
          preview.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
          active = true;
          list.classList.add('is-hovering');
          start();
        });

        list.addEventListener('pointermove', function (e) {
          if (!bounds) bounds = list.getBoundingClientRect();
          targetX = e.clientX - bounds.left;
          targetY = e.clientY - bounds.top;
          start();
        });

        list.addEventListener('pointerleave', function () {
          active = false;
          list.classList.remove('is-hovering');
          list.querySelectorAll('.lf-idx__row').forEach(function (r) {
            r.classList.remove('is-active');
          });
        });
      }

      rows.forEach(function (row) {
        var img = row.dataset.lfImage;
        function activate() {
          rows.forEach(function (r) {
            r.classList.toggle('is-active', r === row);
          });
          if (!img) return;
          preview.querySelectorAll('.lf-idx__preview-img').forEach(function (el) {
            el.classList.toggle('is-shown', el.dataset.lfFor === img);
          });
        }
        row.addEventListener('pointerenter', activate);
        row.addEventListener('focus', activate, true);
      });
    });
  }

  /* ── 6. Rails glissables (drag / inertie) ───────────────── */

  function initDragRails() {
    document.querySelectorAll('[data-lf-drag]').forEach(function (rail) {
      var down = false;
      var moved = false;
      var startX = 0;
      var startScroll = 0;
      var lastX = 0;
      var velocity = 0;
      var glideFrame = null;

      function glide() {
        velocity *= 0.94;
        rail.scrollLeft -= velocity;
        if (Math.abs(velocity) > 0.4) glideFrame = requestAnimationFrame(glide);
        else glideFrame = null;
      }

      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return; // scroll natif sur tactile
        down = true;
        moved = false;
        startX = e.clientX;
        lastX = e.clientX;
        startScroll = rail.scrollLeft;
        velocity = 0;
        if (glideFrame) cancelAnimationFrame(glideFrame);
        rail.classList.add('is-dragging');
      });

      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        velocity = e.clientX - lastX;
        lastX = e.clientX;
        rail.scrollLeft = startScroll - dx;
      });

      function release() {
        if (!down) return;
        down = false;
        rail.classList.remove('is-dragging');
        if (!reduce && Math.abs(velocity) > 1) glideFrame = requestAnimationFrame(glide);
      }

      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('pointerleave', release);

      // Un glissement ne doit pas déclencher le lien sous le curseur.
      rail.addEventListener(
        'click',
        function (e) {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        true
      );
    });
  }

  /* ── 7. Curseur contextuel (« Glisser », « Voir ») ───────── */

  function initCursor() {
    if (isCoarse || reduce) return;
    var zones = document.querySelectorAll('[data-lf-cursor]');
    if (!zones.length) return;

    var cursor = document.createElement('div');
    cursor.className = 'lf-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    var label = document.createElement('span');
    cursor.appendChild(label);
    document.body.appendChild(cursor);

    var tx = 0;
    var ty = 0;
    var cx = 0;
    var cy = 0;
    var visible = false;
    var running = false;

    function loop() {
      cx += (tx - cx) * 0.18;
      cy += (ty - cy) * 0.18;
      cursor.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0) translate(-50%,-50%)';
      if (visible || Math.abs(tx - cx) > 0.5) requestAnimationFrame(loop);
      else running = false;
    }

    function run() {
      if (running) return;
      running = true;
      requestAnimationFrame(loop);
    }

    zones.forEach(function (zone) {
      zone.addEventListener('pointerenter', function () {
        label.textContent = zone.dataset.lfCursor || '';
        visible = true;
        cursor.classList.add('is-visible');
        run();
      });
      zone.addEventListener('pointermove', function (e) {
        tx = e.clientX;
        ty = e.clientY;
        run();
      });
      zone.addEventListener('pointerleave', function () {
        visible = false;
        cursor.classList.remove('is-visible');
      });
    });
  }

  /* ── 8. Dessins techniques : onglets forme + tailles ────── */

  // Deux niveaux d'onglets imbriqués : les sélecteurs sont préfixés pour que
  // le niveau « forme » n'attrape jamais les boutons du niveau « taille ».
  function initBlueprint() {
    document.querySelectorAll('[data-lf-blueprint]').forEach(function (root) {
      var shapeTabs = root.querySelectorAll('[data-lf-bp-tab]');
      var shapePanels = root.querySelectorAll('[data-lf-bp-panel]');

      shapeTabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var key = tab.dataset.lfBpTab;
          shapeTabs.forEach(function (t) {
            t.setAttribute('aria-selected', String(t === tab));
          });
          shapePanels.forEach(function (p) {
            if (p.dataset.lfBpPanel === key) p.removeAttribute('hidden');
            else p.setAttribute('hidden', '');
          });
        });
      });

      // Le changement de taille reste confiné à sa forme : sans cela, choisir
      // une taille masquerait aussi le panneau actif des autres onglets, qui
      // se retrouveraient vides au retour.
      root.querySelectorAll('[data-lf-bp-size]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var group = btn.closest('[data-lf-bp-panel]');
          if (!group) return;
          var key = btn.dataset.lfBpSize;

          group.querySelectorAll('[data-lf-bp-size-panel]').forEach(function (p) {
            if (p.dataset.lfBpSizePanel === key) p.removeAttribute('hidden');
            else p.setAttribute('hidden', '');
          });
          // Chaque taille embarque sa propre barre : on aligne toutes les copies.
          group.querySelectorAll('[data-lf-bp-size]').forEach(function (b) {
            b.setAttribute('aria-selected', String(b.dataset.lfBpSize === key));
          });
        });
      });
    });
  }

  /* ── 9. Nuancier : bascule entre coloris ────────────────── */

  function initColorways() {
    document.querySelectorAll('[data-lf-colorways]').forEach(function (root) {
      var chips = root.querySelectorAll('[data-lf-cw-chip]');
      var shots = root.querySelectorAll('[data-lf-cw-shot]');
      var names = root.querySelectorAll('[data-lf-cw-name]');

      function select(key) {
        chips.forEach(function (c) {
          c.setAttribute('aria-selected', String(c.dataset.lfCwChip === key));
        });
        shots.forEach(function (s) {
          s.classList.toggle('is-shown', s.dataset.lfCwShot === key);
        });
        names.forEach(function (n) {
          n.classList.toggle('is-shown', n.dataset.lfCwName === key);
        });
        root.style.setProperty('--lf-cw-active', root.dataset.tone || '');
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          select(chip.dataset.lfCwChip);
          // La teinte de fond suit le coloris choisi.
          if (chip.dataset.tone) root.style.setProperty('--lf-cw-tone', chip.dataset.tone);
        });
      });
    });
  }

  /* ── 10. Marquee piloté par la vitesse de scroll ────────── */

  // Défilement en JS plutôt qu'en animation CSS : on peut ainsi moduler la
  // vitesse en continu, et inverser le sens quand le visiteur remonte —
  // ce qu'une animation CSS ne sait pas faire sans sauter.
  function initMarquee() {
    var rails = document.querySelectorAll('[data-lf-marquee]');
    if (!rails.length) return;

    rails.forEach(function (rail) {
      var track = rail.querySelector('.lf-marquee__track');
      if (!track) return;

      // On duplique la piste jusqu'à couvrir deux fois la largeur visible,
      // condition d'une boucle sans trou.
      var unit = track.scrollWidth;
      if (!unit) return;
      var needed = Math.ceil((rail.clientWidth * 2) / unit) + 1;
      for (var i = 1; i < needed; i++) {
        var clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.parentElement.appendChild(clone);
      }

      var base = parseFloat(rail.dataset.lfMarquee) || 60; // px/s
      var dir = rail.dataset.lfMarqueeDir === 'rtl' ? -1 : 1;
      var offset = 0;
      var boost = 0;
      var lastY = window.scrollY;
      var lastT = performance.now();
      var inView = true;

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          inView = es[0].isIntersecting;
        }).observe(rail);
      }

      var inner = track.parentElement;

      function frame(now) {
        var dt = Math.min((now - lastT) / 1000, 0.05);
        lastT = now;

        var y = window.scrollY;
        var dy = y - lastY;
        lastY = y;

        // La vitesse de scroll pousse le ruban ; au repos il reprend
        // doucement son allure de croisière.
        var target = Math.max(-3.2, Math.min(3.2, dy / 6));
        boost += (target - boost) * 0.08;

        if (inView && !reduce) {
          offset -= dir * base * (1 + boost) * dt;
          if (offset <= -unit) offset += unit;
          if (offset >= 0) offset -= unit;
          inner.style.transform = 'translate3d(' + offset.toFixed(2) + 'px,0,0)';
        }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    });
  }

  /* ── 11. Témoignage en spotlight ────────────────────────── */

  function initSpotlight() {
    document.querySelectorAll('[data-lf-spotlight]').forEach(function (root) {
      var slides = root.querySelectorAll('[data-lf-sp-slide]');
      if (slides.length < 2) return;
      var i = 0;

      function show(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          s.classList.toggle('is-shown', k === i);
        });
      }

      var prev = root.querySelector('[data-lf-sp-prev]');
      var next = root.querySelector('[data-lf-sp-next]');
      if (prev) {
        prev.addEventListener('click', function () {
          show(i - 1);
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          show(i + 1);
        });
      }
    });
  }

  /* ── 12. Vidéos d'ambiance et mouvement réduit ──────────── */

  /* Les vidéos de fond tournent en boucle et n'offrent aucune commande :
     un visiteur qui demande moins de mouvement n'a aucun moyen de les
     arrêter. On les fige sur leur image d'affiche, et on coupe leur
     préchargement au passage — télécharger plusieurs mégaoctets pour
     afficher une image fixe n'aurait aucun sens.

     Celles qui portent des commandes ne sont pas concernées : elles ont
     déjà leur bouton de lecture, et le visiteur en décide.

     Le héros garde sa vidéo dans un `<template>` et l'injecte après
     coup : un observateur rattrape celles qui arrivent plus tard. Il ne
     tourne que si la préférence est active. */

  var observateurVideo = null;

  function calmerVideo(v) {
    if (v.dataset.lfCalmee === '1') return;
    v.dataset.lfCalmee = '1';
    v.dataset.lfPrecharge = v.getAttribute('preload') || '';
    v.autoplay = false;
    v.removeAttribute('autoplay');
    v.setAttribute('preload', 'none');
    v.pause();
  }

  function relancerVideo(v) {
    if (v.dataset.lfCalmee !== '1') return;
    delete v.dataset.lfCalmee;
    v.autoplay = true;
    v.setAttribute('autoplay', 'autoplay');
    if (v.dataset.lfPrecharge) v.setAttribute('preload', v.dataset.lfPrecharge);
    delete v.dataset.lfPrecharge;
    // Une lecture refusée par le navigateur n'est pas une erreur du thème.
    var essai = v.play();
    if (essai && essai.catch) essai.catch(function () {});
  }

  function videosAmbiance(racine) {
    return (racine || document).querySelectorAll(
      'video[autoplay]:not([controls]), video[data-lf-suspendue]:not([controls]), video[data-lf-calmee]'
    );
  }

  function initVideosCalmes() {
    if (reduceQuery.matches) {
      videosAmbiance().forEach(calmerVideo);
      if (!observateurVideo && window.MutationObserver) {
        observateurVideo = new MutationObserver(function (lots) {
          for (var i = 0; i < lots.length; i++) {
            var ajouts = lots[i].addedNodes;
            for (var j = 0; j < ajouts.length; j++) {
              var n = ajouts[j];
              if (n.nodeType !== 1) continue;
              if (n.tagName === 'VIDEO') {
                if (n.hasAttribute('autoplay') && !n.hasAttribute('controls')) calmerVideo(n);
              } else if (n.querySelectorAll) {
                videosAmbiance(n).forEach(calmerVideo);
              }
            }
          }
        });
        observateurVideo.observe(document.body, { childList: true, subtree: true });
      }
    } else {
      if (observateurVideo) {
        observateurVideo.disconnect();
        observateurVideo = null;
      }
      document.querySelectorAll('video[data-lf-calmee]').forEach(relancerVideo);
    }
  }

  // Le réglage peut changer pendant la visite : le thème suit.
  if (reduceQuery.addEventListener) {
    reduceQuery.addEventListener('change', initVideosCalmes);
  } else if (reduceQuery.addListener) {
    reduceQuery.addListener(initVideosCalmes);
  }

  /* ── 13. Chargement differe des vidéos ──────────────────── */

  /* Le balisage garde `autoplay` : c'est lui qui garantit la lecture.
     Ce qui suit ne fait qu'alleger — on retire l'autoplay des videos
     encore loin de l'ecran, et on le rend a leur approche. Si le calcul
     de fenetre echoue, on ne touche a rien et la video joue comme le
     navigateur l'entend. Le defaut possible est un telechargement en
     avance, jamais une video qui ne demarre pas. */

  function rendreVideo(v) {
    if (v.dataset.lfSuspendue !== '1') return;
    delete v.dataset.lfSuspendue;
    v.preload = 'metadata';
    v.autoplay = true;
    v.setAttribute('autoplay', 'autoplay');
    v.load();
    // iOS refuse parfois une lecture demandee avant que le media soit
    // pret : on repasse une fois qu'il l'est.
    function tenter() {
      var essai = v.play();
      if (essai && essai.catch) essai.catch(function () {});
    }
    v.addEventListener('canplay', tenter, { once: true });
    tenter();
  }

  function initVideosDifferees() {
    var attente = [];
    var vh = window.innerHeight;
    // Une fenetre nulle ou absurde : on ne suspend rien.
    if (!vh || vh < 200) return;

    /* On reprend aussi celles deja suspendues : `init` se rejoue a chaque
       `shopify:section:load` en vidant la boucle, et sans cela une video
       suspendue plus tot cessait d'etre suivie — donc de repartir. */
    document.querySelectorAll('video[data-lf-differee], video[data-lf-suspendue]').forEach(function (v) {
      delete v.dataset.lfDifferee;
      var r = v.getBoundingClientRect();
      // Deja proche : on la laisse telle quelle, elle joue toute seule.
      if (r.top < vh * 2 && r.bottom > -vh) return;
      v.dataset.lfSuspendue = '1';
      v.autoplay = false;
      v.removeAttribute('autoplay');
      v.preload = 'none';
      attente.push(v);
    });
    if (!attente.length) return;

    scrubbed.push(function (h) {
      for (var i = attente.length - 1; i >= 0; i--) {
        var r = attente[i].getBoundingClientRect();
        if (r.top < h * 2 && r.bottom > -h) rendreVideo(attente.splice(i, 1)[0]);
      }
    });
  }

  /* ── Init ───────────────────────────────────────────────── */

  function init() {
    scrubbed.length = 0;
    initMega();
    initSplit();
    initReveal();
    initScrub();
    initParallax();
    initIndexList();
    initDragRails();
    initCursor();
    initBlueprint();
    initColorways();
    initMarquee();
    initSpotlight();
    initVideosCalmes();
    initVideosDifferees();
    schedule();
    // premier calcul après mise en page des polices
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () {
    scrubbed.length = 0;
  });
})();
